import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import i18next from "i18next";
import { LocalePathProvider } from "../contexts/LocalePathContext";
import NotFound from "../Pages/NotFound/NotFound";
import {
  buildUsToEnRedirectPath,
  isExcludedFromLocalePrefix,
  isValidLocaleUrlPrefix,
  parseLocaleFromPathname,
  shouldRedirectToLocalePrefix,
  shouldRedirectUsPrefixToEn,
  urlPrefixToI18nLanguage,
} from "../utils/localeUrl";

function syncDocumentLanguage(i18nLng) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = i18nLng;
  document.documentElement.dir = i18nLng === "he" ? "rtl" : "ltr";
  try {
    localStorage.setItem("i18nextLng", i18nLng);
  } catch {
    /* ignore */
  }
}

/**
 * Wraps public routes with /he/ or /en/ prefix handling.
 */
export function LocaleAwarePublicRoutes({ children }) {
  const location = useLocation();

  if (shouldRedirectUsPrefixToEn(location.pathname)) {
    return (
      <Navigate
        to={buildUsToEnRedirectPath(location.pathname, location.search, location.hash)}
        replace
      />
    );
  }

  const { localePrefix } = parseLocaleFromPathname(location.pathname);

  useEffect(() => {
    if (!isValidLocaleUrlPrefix(localePrefix)) return;
    const i18nLng = urlPrefixToI18nLanguage(localePrefix);
    i18next.changeLanguage(i18nLng).finally(() => syncDocumentLanguage(i18nLng));
  }, [localePrefix, location.pathname]);

  if (shouldRedirectToLocalePrefix(location.pathname)) {
    const targetPath = `/he${location.pathname === "/" ? "/" : location.pathname}`;
    return <Navigate to={`${targetPath}${location.search}${location.hash}`} replace />;
  }

  if (!isValidLocaleUrlPrefix(localePrefix)) {
    if (isExcludedFromLocalePrefix(location.pathname)) {
      return <LocalePathProvider localePrefix={null}>{children}</LocalePathProvider>;
    }
    return <NotFound />;
  }

  return <LocalePathProvider localePrefix={localePrefix}>{children}</LocalePathProvider>;
}
